import { faCircle2 as faCircle2Regular } from '@fortawesome/pro-regular-svg-icons';
import {
  faCircle1,
  faCircle2 as faCircle2Solid,
  faCircleCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs';
import React from 'react';
import { FormProvider } from 'react-hook-form';
import CalculationResultWrapper from '~/components/Tools/CalculateMaximumMortgage/CalculationResultWrapper';
import CalculationSearch from '~/components/Tools/CalculateMaximumMortgage/CalculationSearch';
import IncomeStep from '~/components/Tools/CalculateMaximumMortgage/IncomeStep';
import ObligationsStep from '~/components/Tools/CalculateMaximumMortgage/ObligationsStep';
import ProviderMatches from '~/components/Tools/CalculateMaximumMortgage/ProviderMatches';
import Panel from '~/components/Tools/ToolPanel';
import { cn } from '~/helpers/utils';
import { useCalculateMaximumMortgageCalculation } from '~/pages/Tools/CalculateMaximumMortgage/Calculate/context/CalculationContext';
import { useCalculateMaximumMortgageForm } from '~/pages/Tools/CalculateMaximumMortgage/Calculate/context/FormProvider';
import { useCalculateMaximumMortgageTabs } from '~/pages/Tools/CalculateMaximumMortgage/Calculate/context/TabsContext';
import { OfficeDetails } from '~/types/Office';
import CalculationOffice from './CalculationOffice';

type CalculateMaximumMortgageFormProps = {
  totalOffices: number;
  totalReviews: number;
  office?: OfficeDetails;
};

const CalculateMaximumMortgageForm: React.FC<
  CalculateMaximumMortgageFormProps
> = ({ totalOffices, totalReviews, office }) => {
  const {
    isCalculating,
    providerMatches,
    selected,
    handleCalculation,
    hasCalculated,
  } = useCalculateMaximumMortgageCalculation();

  const form = useCalculateMaximumMortgageForm();
  const onSubmit = form.handleSubmit((data) => handleCalculation(data));

  const { activeTab, setActiveTab } = useCalculateMaximumMortgageTabs();

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit}>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-6 xl:grid-cols-3">
          <div>
            <Panel>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-2 gap-3 border-b-2 border-stone-50 pb-6">
                  <TabsTrigger
                    className="pointer-events-none flex cursor-default items-center gap-2 font-display font-bold text-blue-600"
                    value="incomes"
                  >
                    <FontAwesomeIcon
                      className="text-xl text-blue-500"
                      icon={activeTab === 'incomes' ? faCircle1 : faCircleCheck}
                    />
                    Inkomen
                    <span className="h-0.5 flex-grow bg-blue-400" />
                  </TabsTrigger>
                  <TabsTrigger
                    className={cn(
                      'pointer-events-none flex cursor-default items-center gap-2 font-display font-bold',
                      activeTab === 'obligations'
                        ? 'text-blue-600'
                        : 'text-stone-200'
                    )}
                    value="obligations"
                  >
                    <FontAwesomeIcon
                      className={cn(
                        'text-xl',
                        activeTab === 'obligations'
                          ? 'text-blue-500'
                          : 'text-stone-200'
                      )}
                      icon={
                        activeTab === 'obligations'
                          ? hasCalculated
                            ? faCircleCheck
                            : faCircle2Solid
                          : faCircle2Regular
                      }
                    />
                    Verplichtingen
                    <span
                      className={cn(
                        'h-0.5 flex-grow',
                        activeTab === 'incomes' ? 'bg-stone-200' : 'bg-blue-400'
                      )}
                    />
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="incomes" className="focus:outline-none">
                  <IncomeStep
                    onHandleNext={() => setActiveTab('obligations')}
                  />
                </TabsContent>
                <TabsContent value="obligations" className="focus:outline-none">
                  <ObligationsStep
                    hasResult={!!selected}
                    onHandlePrev={() => setActiveTab('incomes')}
                  />
                </TabsContent>
              </Tabs>
            </Panel>
          </div>
          <div>
            <Panel>
              <CalculationResultWrapper />
              {office ? (
                <CalculationOffice office={office} />
              ) : (
                <CalculationSearch
                  totalOffices={totalOffices}
                  totalReviews={totalReviews}
                />
              )}
            </Panel>
          </div>
          {!isCalculating && providerMatches.length > 0 && (
            <div className="hidden xl:block">
              <Panel>
                <ProviderMatches />
              </Panel>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default CalculateMaximumMortgageForm;
